import React, { useState } from "react";
import Resources from "../../../../types/Resources";
import PositionPickerRow from "../../../../types/forms/Permits/PositionPickerRow";
import TextInput from "../../../Common/TextInput";
import Button from "../../../Common/Button";
import { PermitCategory } from "../../../../common/enums";
import DrawPolygonModal from "./DrawPolygonModal";
import CoordinatesModal from "./CoordinatesModal";

type Props = {
    showUpdatePositionsButton: boolean;
    showActiveStatus: boolean;
    showCategories: boolean;
    positions: PositionPickerRow[];
    canManagePositionIsActive: boolean;
    canManagePositions: boolean;
    canManageComments: boolean;
    canManagePolygons: boolean;
    canManageCategories: boolean;
    selectedCategories: PermitCategory[];
    onPositionChange: (updatedPosition: PositionPickerRow) => void;
    onPositionAdded: (newPositionRow: PositionPickerRow) => void;
    onPositionRemoved: (removedPositionRow: PositionPickerRow) => void;
    onUpdatePositionsClicked: () => void;
    resources: Resources;
};

function PositionPicker(props: Props) {
    const [modalPositionIndex, setModalPositionIndex] = useState(0);
    const [showDrawPolygonModal, setShowDrawPolygonModal] = useState(false);
    const [showCoordinatesModal, setShowCoordinatesModal] = useState(false);

    const { resources, selectedCategories } = props;

    function polygonMapClickHandler(position: PositionPickerRow) {
        setModalPositionIndex(position.order);
        setShowDrawPolygonModal(true);
    }

    function coordinatedMapsClickHandler(position: PositionPickerRow) {
        setModalPositionIndex(position.order);
        setShowCoordinatesModal(true);
    }

    function addPositionClickHandler(clickedPositionRow: PositionPickerRow) {
        const newPositionRow: PositionPickerRow = {
            id: 0,
            comment: "",
            order: clickedPositionRow.order + 1,
            isActive: false,
            latitude: undefined,
            longitude: undefined,
            polygon: null,
            category: undefined
        };

        props.onPositionAdded(newPositionRow);
    }

    const isColdWorkEnabled = selectedCategories.includes(PermitCategory.ColdWork);
    const isHotWorkCategoryOneEnabled = selectedCategories.includes(PermitCategory.HotWorkCategoryOne);
    const isHotWorkCategoryTwoEnabled = selectedCategories.includes(PermitCategory.HotWorkCategoryTwo);
    const isHotWorkCategoryThreeEnabled = selectedCategories.includes(PermitCategory.HotWorkCategoryThree);

    const modalPosition =
        showCoordinatesModal || showDrawPolygonModal
            ? props.positions.find((p) => p.order === modalPositionIndex)
            : undefined;

    return (
        <>
            <div className="mt-2 pb-2 pt-2 rounded-3 text-center" style={{ backgroundColor: "#F7F9FA" }}>
                <div className="position-relative">
                    <div
                        className="position-absolute d-flex align-items-start"
                        style={{
                            top: 10,
                            left: 10
                        }}
                    >
                        {props.showUpdatePositionsButton ? (
                            <button type="button" className="btn btn-primary" onClick={props.onUpdatePositionsClicked}>
                                {props.resources.get("UpdatePositions")}
                            </button>
                        ) : null}
                    </div>
                    <h2 className="h4">{resources.get("PositionHeader")}</h2>
                </div>
                <div className="mb-3">
                    <div className="d-block">{resources.get("PositionInformation1")}</div>
                    <span>{resources.get("PositionInformation2")}</span>
                    <i className=" ms-1 me-1 fas fa-location-dot"></i>
                    <span>{resources.get("PositionInformation3")}</span>
                </div>
                <table className="w-100">
                    <thead>
                        <tr>
                            {props.showActiveStatus ? <th>{resources.get("Active")}</th> : null}
                            <th>{resources.get("Latitude")}</th>
                            <th>{resources.get("Longitude")}</th>
                            <th></th>
                            <th>
                                {resources.get("Comment")} / {resources.get("Location")}
                            </th>
                            {props.showCategories ? (
                                <>
                                    <th>{resources.get("ColdWorkCat")}</th>
                                    <th>{resources.get("HotWorkCat1")}</th>
                                    <th>{resources.get("HotWorkCat2")}</th>
                                    <th>{resources.get("HotWorkCat3")}</th>
                                </>
                            ) : null}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.positions.map((position, index) => (
                            <PositionRow
                                key={index}
                                position={position}
                                resources={resources}
                                canManagePositions={props.canManagePositions}
                                canManageComments={props.canManageComments}
                                canManagePolygons={props.canManagePolygons}
                                canManageCategories={props.canManageCategories}
                                showActiveStatus={props.showActiveStatus}
                                isColdWorkEnabled={isColdWorkEnabled}
                                isHotWorkCategoryOneEnabled={isHotWorkCategoryOneEnabled}
                                isHotWorkCategoryTwoEnabled={isHotWorkCategoryTwoEnabled}
                                isHotWorkCategoryThreeEnabled={isHotWorkCategoryThreeEnabled}
                                showCategories={props.showCategories}
                                onOpenPolygonMap={polygonMapClickHandler}
                                onOpenCoordinatesMap={coordinatedMapsClickHandler}
                                onPositionChange={props.onPositionChange}
                                onAddPosition={addPositionClickHandler}
                                onRemovePosition={props.onPositionRemoved}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            <CoordinatesModal
                onClose={() => setShowCoordinatesModal(false)}
                onPositionChange={props.onPositionChange}
                position={modalPosition}
                show={showCoordinatesModal && !!modalPosition}
                resources={props.resources}
            />
            <DrawPolygonModal
                onClose={() => setShowDrawPolygonModal(false)}
                onPositionUpdate={props.onPositionChange}
                show={showDrawPolygonModal && !!modalPosition}
                position={modalPosition}
                resources={props.resources}
            />
        </>
    );
}

type PositionRowProps = {
    position: PositionPickerRow;
    isColdWorkEnabled: boolean;
    isHotWorkCategoryOneEnabled: boolean;
    isHotWorkCategoryTwoEnabled: boolean;
    isHotWorkCategoryThreeEnabled: boolean;
    resources: Resources;
    canManagePositions: boolean;
    canManageComments: boolean;
    canManagePolygons: boolean;
    canManageCategories: boolean;
    showActiveStatus: boolean;
    showCategories: boolean;
    onOpenPolygonMap: (position: PositionPickerRow) => void;
    onOpenCoordinatesMap: (position: PositionPickerRow) => void;
    onPositionChange: (updatedPosition: PositionPickerRow) => void;
    onAddPosition: (clickedPosition: PositionPickerRow) => void;
    onRemovePosition: (clickedPosition: PositionPickerRow) => void;
};

function PositionRow(props: PositionRowProps) {
    const { position, resources } = props;

    const isColdWorkDisabled = !props.isColdWorkEnabled || !props.canManageCategories;
    const isHotWorkCatOneDisabled = !props.isHotWorkCategoryOneEnabled || !props.canManageCategories;
    const isHotWorkCatTwoDisabled = !props.isHotWorkCategoryTwoEnabled || !props.canManageCategories;
    const isHotWorkCatThreeDisabled = !props.isHotWorkCategoryThreeEnabled || !props.canManageCategories;

    return (
        <tr>
            {props.showActiveStatus ? (
                <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={position.isActive}
                        onChange={(e) => props.onPositionChange({ ...position, isActive: e.target.checked })}
                    />
                </td>
            ) : null}
            <td style={{ maxWidth: "100px" }}>
                <TextInput
                    inputId={`latitude-${position.order}`}
                    type="number"
                    labelText={undefined}
                    value={position.latitude}
                    onChange={(updatedString) =>
                        props.onPositionChange({
                            ...position,
                            latitude: updatedString !== "" ? Number.parseFloat(updatedString) : undefined
                        })
                    }
                    readOnly={!props.canManagePositions}
                    errorMessage={position.latitudeErrorMessage}
                />
            </td>
            <td style={{ maxWidth: "100px" }}>
                <TextInput
                    inputId={`longitude-${position.order}`}
                    type="number"
                    labelText={undefined}
                    value={position.longitude}
                    onChange={(updatedString) =>
                        props.onPositionChange({
                            ...position,
                            longitude: updatedString !== "" ? Number.parseFloat(updatedString) : undefined
                        })
                    }
                    readOnly={!props.canManagePositions}
                    errorMessage={position.longitudeErrorMessage}
                />
            </td>
            <td className="text-nowrap">
                <Button
                    className="btn btn-secondary me-1"
                    type="button"
                    disabled={!props.canManagePolygons}
                    onClick={() => props.onOpenPolygonMap(position)}
                    tooltip={props.canManagePolygons ? "Rita område" : undefined}
                >
                    <i className="fas fa-paintbrush"></i>
                </Button>
                <Button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => props.onOpenCoordinatesMap(position)}
                    disabled={!props.canManagePositions}
                    tooltip={props.canManagePositions ? "Uppdatera koordinater" : undefined}
                >
                    <i className="fas fa-location-dot"></i>
                </Button>
            </td>
            <td>
                <TextInput
                    inputId={`comment-${position.order}`}
                    labelText={undefined}
                    value={position.comment}
                    onChange={(updatedComment) => props.onPositionChange({ ...position, comment: updatedComment })}
                    readOnly={!props.canManageComments}
                />
            </td>
            {props.showCategories ? (
                <>
                    <td>
                        <input
                            type="checkbox"
                            className={`form-check-input ${isColdWorkDisabled ? "read-only-check" : ""}`}
                            checked={position.category === PermitCategory.ColdWork}
                            onChange={(e) =>
                                props.onPositionChange({
                                    ...position,
                                    category: e.target.checked ? PermitCategory.ColdWork : undefined
                                })
                            }
                            disabled={!props.canManageCategories || !props.isColdWorkEnabled}
                            name={`category-${position.order}`}
                            aria-label={resources.get(PermitCategory.ColdWork)}
                        />
                    </td>
                    <td>
                        <input
                            type="checkbox"
                            className={`form-check-input ${isHotWorkCatOneDisabled ? "read-only-check" : ""}`}
                            checked={position.category === PermitCategory.HotWorkCategoryOne}
                            onChange={(e) =>
                                props.onPositionChange({
                                    ...position,
                                    category: e.target.checked ? PermitCategory.HotWorkCategoryOne : undefined
                                })
                            }
                            disabled={isHotWorkCatOneDisabled}
                            name={`category-${position.order}`}
                            aria-label={resources.get(PermitCategory.HotWorkCategoryOne)}
                        />
                    </td>
                    <td>
                        <input
                            type="checkbox"
                            className={`form-check-input ${isHotWorkCatTwoDisabled ? "read-only-check" : ""}`}
                            onChange={(e) =>
                                props.onPositionChange({
                                    ...position,
                                    category: e.target.checked ? PermitCategory.HotWorkCategoryTwo : undefined
                                })
                            }
                            checked={position.category === PermitCategory.HotWorkCategoryTwo}
                            disabled={isHotWorkCatTwoDisabled}
                            name={`category-${position.order}`}
                            aria-label={resources.get(PermitCategory.HotWorkCategoryTwo)}
                        />
                    </td>
                    <td>
                        <input
                            type="checkbox"
                            className={`form-check-input ${isHotWorkCatThreeDisabled ? "read-only-check" : ""}`}
                            onChange={(e) =>
                                props.onPositionChange({
                                    ...position,
                                    category: e.target.checked ? PermitCategory.HotWorkCategoryThree : undefined
                                })
                            }
                            checked={position.category === PermitCategory.HotWorkCategoryThree}
                            disabled={isHotWorkCatThreeDisabled}
                            name={`category-${position.order}`}
                            aria-label={resources.get(PermitCategory.HotWorkCategoryThree)}
                        />
                    </td>
                </>
            ) : null}

            {props.canManagePositions ? (
                <td>
                    <Button
                        className="btn btn-secondary me-1"
                        type="button"
                        tooltip="Lägg till ny position"
                        onClick={() => props.onAddPosition(position)}
                    >
                        <i className="fas fa-plus"></i>
                    </Button>
                    {position.order > 0 && (
                        <Button
                            className="btn btn-secondary"
                            type="button"
                            tooltip="Ta bort position"
                            onClick={() => props.onRemovePosition(position)}
                        >
                            <i className="fas fa-minus"></i>
                        </Button>
                    )}
                </td>
            ) : null}
        </tr>
    );
}

export default PositionPicker;
